import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { itineraryApi } from '../services/api';
import { Loader2, Check, Luggage, Sun, Shirt, Pill, FileText, Printer, ArrowLeft } from 'lucide-react';

const CLIMATES = [
  { value: 'tropical', label: 'Tropical / Hot & Humid' },
  { value: 'temperate', label: 'Temperate / Mild' },
  { value: 'cold', label: 'Cold / Winter' },
  { value: 'desert', label: 'Desert / Hot & Dry' },
  { value: 'mixed', label: 'Mixed / Variable' },
];

const TRIP_TYPES = [
  { value: 'leisure', label: 'Leisure / Sightseeing' },
  { value: 'adventure', label: 'Adventure / Hiking' },
  { value: 'business', label: 'Business Travel' },
  { value: 'beach', label: 'Beach Holiday' },
  { value: 'cultural', label: 'Cultural / Historical' },
  { value: 'family', label: 'Family Vacation' },
];

const PackingListPage = () => {
  const navigate = useNavigate();
  const [destination, setDestination] = useState('');
  const [duration, setDuration] = useState('7');
  const [climate, setClimate] = useState('');
  const [tripType, setTripType] = useState('');
  const [loading, setLoading] = useState(false);
  const [packingList, setPackingList] = useState(null);
  const [checkedItems, setCheckedItems] = useState({});

  const handleGenerate = async () => {
    if (!destination || !climate || !tripType) {
      toast.error('Please fill in all fields');
      return;
    }
    setLoading(true);
    try {
      const response = await itineraryApi.generatePackingList({ destination, duration: parseInt(duration), climate, trip_type: tripType });
      if (response.data.success) {
        setPackingList(response.data.packing_list);
        setCheckedItems({});
        toast.success('Packing list generated!');
      } else {
        toast.error(response.data.error || 'Failed to generate packing list');
      }
    } catch (error) {
      toast.error('Failed to generate packing list');
    } finally {
      setLoading(false);
    }
  };

  const toggleItem = (category, index) => {
    const key = `${category}-${index}`;
    setCheckedItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const getCategoryIcon = (category) => {
    const lower = category.toLowerCase();
    if (lower.includes('cloth') || lower.includes('wear')) return <Shirt className="w-5 h-5" />;
    if (lower.includes('toiletri') || lower.includes('health') || lower.includes('medic')) return <Pill className="w-5 h-5" />;
    if (lower.includes('document') || lower.includes('essent')) return <FileText className="w-5 h-5" />;
    if (lower.includes('weather') || lower.includes('sun')) return <Sun className="w-5 h-5" />;
    return <Luggage className="w-5 h-5" />;
  };

  const handlePrint = () => {
    window.print();
  };

  const totalItems = packingList ? Object.values(packingList).flat().length : 0;
  const packedItems = Object.values(checkedItems).filter(Boolean).length;

  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="packing-list-page">
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a2744] via-[#1e3a5f] to-[#0d4f6f]" />
        <div className="absolute top-40 left-16 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-32 right-20 w-80 h-80 bg-gold/10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '3s'}} />
      </div>
      <Header />
      <main className="pt-24 pb-16 px-6 md:px-12 lg:px-24 relative z-10">
        <div className="max-w-4xl mx-auto">
          <div className="mb-6">
            <button onClick={() => navigate('/dashboard')} className="text-white/70 hover:text-white text-sm flex items-center gap-1 transition-colors" data-testid="back-to-dashboard-top">
              <ArrowLeft className="w-4 h-4" /> Back to Dashboard
            </button>
          </div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10">
            <div className="w-16 h-16 bg-gold/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Luggage className="w-8 h-8 text-gold" />
            </div>
            <h1 className="font-serif text-3xl md:text-4xl text-white drop-shadow-lg mb-2">AI Packing List Generator</h1>
            <p className="text-white/70">Never forget an essential item again</p>
          </motion.div>

          {/* Form */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-xl border border-white/30 p-8 mb-8">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label className="text-gray-700 font-medium">Destination</Label>
                <Input value={destination} onChange={(e) => setDestination(e.target.value)} placeholder="e.g., Tokyo, Japan"
                  className="mt-2 h-12 border-gray-200" data-testid="packing-destination-input" />
              </div>
              <div>
                <Label className="text-gray-700 font-medium">Duration (days)</Label>
                <Input type="number" value={duration} onChange={(e) => setDuration(e.target.value)} min="1" max="60"
                  className="mt-2 h-12 border-gray-200" data-testid="packing-duration-input" />
              </div>
              <div>
                <Label className="text-gray-700 font-medium">Climate</Label>
                <Select value={climate} onValueChange={setClimate}>
                  <SelectTrigger className="mt-2 h-12 border-gray-200" data-testid="packing-climate-select">
                    <SelectValue placeholder="Select climate" />
                  </SelectTrigger>
                  <SelectContent>
                    {CLIMATES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-700 font-medium">Trip Type</Label>
                <Select value={tripType} onValueChange={setTripType}>
                  <SelectTrigger className="mt-2 h-12 border-gray-200" data-testid="packing-type-select">
                    <SelectValue placeholder="Select trip type" />
                  </SelectTrigger>
                  <SelectContent>
                    {TRIP_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button className="mt-6 w-full h-12 bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-full font-semibold hover:from-teal-700 hover:to-teal-800"
              onClick={handleGenerate} disabled={loading} data-testid="generate-packing-list-btn">
              {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Luggage className="w-5 h-5 mr-2" />}
              {loading ? 'Generating...' : 'Generate Packing List'}
            </Button>
          </motion.div>

          {/* Packing List Results */}
          {packingList && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="font-serif text-2xl text-white drop-shadow-lg">Your Packing List</h2>
                  <p className="text-white/70 text-sm">{packedItems} of {totalItems} items packed</p>
                </div>
                <Button variant="outline" onClick={handlePrint} className="border-teal-300 text-teal-700" data-testid="print-packing-list-btn">
                  <Printer className="w-4 h-4 mr-2" /> Print
                </Button>
              </div>

              {/* Progress bar */}
              <div className="w-full bg-gray-200 rounded-full h-3 mb-8">
                <div className="bg-gradient-to-r from-teal-500 to-amber-400 h-3 rounded-full transition-all duration-500"
                  style={{ width: `${totalItems > 0 ? (packedItems / totalItems) * 100 : 0}%` }} />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {Object.entries(packingList).map(([category, items], catIdx) => (
                  <div key={category} className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-lg border border-white/30 p-6 hover:shadow-xl transition-shadow">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-teal-50 rounded-xl flex items-center justify-center text-teal-600">
                        {getCategoryIcon(category)}
                      </div>
                      <h3 className="font-semibold text-gray-900 capitalize">{category.replace(/_/g, ' ')}</h3>
                    </div>
                    <ul className="space-y-2">
                      {(Array.isArray(items) ? items : [items]).map((item, i) => {
                        const key = `${category}-${i}`;
                        const isChecked = checkedItems[key];
                        return (
                          <li key={key} className="flex items-center gap-3 cursor-pointer group" onClick={() => toggleItem(category, i)}
                            data-testid={`packing-item-${catIdx}-${i}`}>
                            <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${
                              isChecked ? 'bg-teal-500 border-teal-500' : 'border-gray-300 group-hover:border-teal-400'}`}>
                              {isChecked && <Check className="w-3 h-3 text-white" />}
                            </div>
                            <span className={`text-sm transition-all ${isChecked ? 'text-gray-400 line-through' : 'text-gray-700'}`}>
                              {typeof item === 'string' ? item : item.name || item.item || JSON.stringify(item)}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          <div className="flex justify-center mt-8">
            <button onClick={() => navigate('/dashboard')} className="text-white/70 hover:text-white border border-white/30 hover:bg-white/10 rounded-full px-6 py-2 text-sm font-medium flex items-center gap-2 transition-all" data-testid="back-to-dashboard-bottom">
              <ArrowLeft className="w-4 h-4" /> Back to Dashboard
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PackingListPage;
